import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Avatar from "../components/Avatar";
import PostCard from "../components/PostCard";
import { Users, MessageSquare, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const boxCls = "bg-[#121212] border border-[#262626] rounded-sm";

export default function GuardianPage() {
  const [children, setChildren] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [activity, setActivity] = useState(null);
  const [needsConfirm, setNeedsConfirm] = useState(false);

  const activeChild = children?.find((c) => c.id === activeId);

  const confirmNow = async () => {
    await api.post(`/guardian/children/${activeId}/confirm`);
    setChildren((cs) => cs.map((c) => (c.id === activeId ? { ...c, guardian_confirmed: true } : c)));
    setNeedsConfirm(false);
    api.get(`/guardian/children/${activeId}/activity`).then(({ data }) => setActivity(data));
  };

  const resendEmail = async () => {
    const { data } = await api.post(`/guardian/children/${activeId}/resend`);
    toast[data.email_sent ? "success" : "info"](
      data.email_sent
        ? "Confirmation email sent"
        : "Email sending is not configured yet — use the Confirm button instead"
    );
  };

  useEffect(() => {
    api.get("/guardian/children").then(({ data }) => {
      setChildren(data);
      if (data.length > 0) setActiveId(data[0].id);
    });
  }, []);

  useEffect(() => {
    if (!activeId) return;
    setActivity(null);
    setNeedsConfirm(false);
    api
      .get(`/guardian/children/${activeId}/activity`)
      .then(({ data }) => setActivity(data))
      .catch((err) => {
        if (err.response?.data?.detail === "guardian_confirmation_required") setNeedsConfirm(true);
      });
  }, [activeId]);

  if (children === null) return <div className="max-w-4xl mx-auto p-8 text-[#737373]">Loading…</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-4" data-testid="guardian-page">
      <h1 className="font-display text-4xl uppercase tracking-tight text-white flex items-center gap-3">
        <Users className="w-8 h-8" /> Family
      </h1>
      <p className="text-sm text-[#a3a3a3]">
        Youth accounts linked to your email. You can see everything they do on EGNCY.
      </p>

      {children.length === 0 ? (
        <div className={`${boxCls} p-8 text-center text-[#a3a3a3] text-sm`} data-testid="guardian-empty">
          No youth accounts are linked to your email yet. When your child signs up with your email
          as their parent contact, they'll appear here.
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            {children.map((c) => (
              <button
                key={c.id}
                data-testid={`guardian-child-${c.id}`}
                onClick={() => setActiveId(c.id)}
                className={`flex items-center gap-2 px-3 py-2 border rounded-sm text-sm transition-colors ${
                  activeId === c.id ? "border-white bg-[#1a1a1a] text-white" : "border-[#262626] text-[#a3a3a3] hover:text-white"
                }`}
              >
                <Avatar url={c.avatar_url} name={c.name} size={24} />
                {c.name}
              </button>
            ))}
          </div>

          {needsConfirm ? (
            <div className={`${boxCls} p-6`} data-testid="guardian-confirm-required">
              <div className="text-white font-semibold">Confirm you are {activeChild?.name}'s parent/guardian</div>
              <p className="text-sm text-[#a3a3a3] mt-1">
                For your child's safety, guardian access must be confirmed before you can see their activity.
              </p>
              <div className="flex gap-2 mt-4">
                <button
                  data-testid="guardian-confirm-btn"
                  onClick={confirmNow}
                  className="px-5 py-2 bg-white text-black font-semibold rounded-sm hover:bg-gray-200 text-sm"
                >
                  Confirm access
                </button>
                <button
                  data-testid="guardian-resend-btn"
                  onClick={resendEmail}
                  className="px-5 py-2 border border-[#404040] text-white rounded-sm hover:bg-[#1a1a1a] text-sm"
                >
                  Resend confirmation email
                </button>
              </div>
            </div>
          ) : !activity ? (
            <div className="text-[#737373] p-4">Loading activity…</div>
          ) : (
            <div className="space-y-4" data-testid="guardian-activity">
              <div className={`${boxCls} p-4 flex items-center gap-3`}>
                <Avatar url={activity.child.avatar_url} name={activity.child.name} size={44} />
                <div>
                  <Link to={`/profile/${activity.child.id}`} className="text-white font-semibold hover:underline">
                    {activity.child.name}
                  </Link>
                  <div className="text-xs text-[#a3a3a3]">
                    {activity.child.position || "Player"}
                    {activity.child.team ? ` · ${activity.child.team}` : ""} ·{" "}
                    {activity.child.account_type === "parent_run" ? "Parent-run account" : "Youth account"}
                  </div>
                </div>
              </div>

              <section className={`${boxCls} p-4`} data-testid="guardian-conversations">
                <h2 className="font-display text-lg uppercase tracking-widest text-white mb-3 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" /> Messages ({activity.conversations.length})
                </h2>
                {activity.conversations.length === 0 ? (
                  <div className="text-xs text-[#737373]">No conversations yet.</div>
                ) : (
                  activity.conversations.map((c) => (
                    <details key={c.id} className="border border-[#262626] rounded-sm mb-2">
                      <summary className="p-3 text-sm text-white cursor-pointer">
                        With {c.other?.name || "Unknown"} <span className="text-[#737373]">({c.other?.role})</span> — {c.messages.length} messages
                      </summary>
                      <div className="p-3 pt-0 space-y-1 max-h-64 overflow-y-auto">
                        {c.messages.map((m, i) => (
                          <div key={i} className={`text-xs p-2 rounded-sm ${m.from_child ? "bg-[#1a1a1a] text-white" : "bg-[#0a0a0a] text-[#a3a3a3] border border-[#262626]"}`}>
                            <span className="text-[#737373] mr-2">{m.from_child ? activity.child.name : c.other?.name}:</span>
                            {m.text}
                          </div>
                        ))}
                      </div>
                    </details>
                  ))
                )}
                {activity.message_requests.length > 0 && (
                  <div className="mt-3">
                    <div className="text-[10px] uppercase tracking-widest text-[#737373] mb-1">Message requests received</div>
                    {activity.message_requests.map((r, i) => (
                      <div key={i} className="text-xs text-[#a3a3a3] py-1">
                        {r.from?.name} ({r.from?.role}) — <span className="text-white">{r.status}</span>
                        {r.text ? ` · “${r.text}”` : ""}
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <section className={`${boxCls} p-4`} data-testid="guardian-comments">
                <h2 className="font-display text-lg uppercase tracking-widest text-white mb-3 flex items-center gap-2">
                  <MessageCircle className="w-4 h-4" /> Comments made ({activity.comments.length})
                </h2>
                {activity.comments.length === 0 ? (
                  <div className="text-xs text-[#737373]">No comments yet.</div>
                ) : (
                  activity.comments.map((c, i) => (
                    <div key={i} className="text-xs text-[#a3a3a3] py-1 border-b border-[#262626] last:border-0">
                      “{c.text}” — <Link to={`/p/${c.post_id}`} className="text-white hover:underline">view post</Link>
                    </div>
                  ))
                )}
              </section>

              <section data-testid="guardian-posts">
                <h2 className="font-display text-lg uppercase tracking-widest text-white mb-2">
                  Posts ({activity.posts.length})
                </h2>
                <div className="space-y-4">
                  {activity.posts.map((p) => <PostCard key={p.id} post={p} />)}
                </div>
              </section>
            </div>
          )}
        </>
      )}
    </div>
  );
}
